import React, { useState, useMemo } from 'react';
import {
  Stack,
  Paper,
  Group,
  Button,
  Grid,
  Select,
  Text,
  Loader,
  Center,
  Alert,
  SegmentedControl,
  Badge
} from '@mantine/core';
import { IconDownload, IconChartBar, IconFilter, IconFileSpreadsheet } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { DatePickerInput } from '@mantine/dates';

import PageTitle from 'components/PageTitle';
import {
  yearlyCalendarReportService,
  YearlyCalendarReportFilters,
  YearlyCalendarCourseReport
} from 'services/yearlyCalendar/reportService';
import ReadOnlyCalendarScheduleView from '../SchedulePlans/components/ReadOnlyCalendarScheduleView';
import ReportStatistics from '../SchedulePlans/components/ReportStatistics';
import { masterAreaService } from 'services/dacum/masterAreaService';

const formatDateToLocalString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseLocalDateString = (dateString: string): Date | null => {
  if (!dateString) return null;

  const [yearStr, monthStr, dayStr] = dateString.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);

  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
    return null;
  }

  return new Date(year, month - 1, day);
};

const ReportsPage: React.FC = () => {
  const [viewMode, setViewMode] = useState<'calendar' | 'statistics'>('calendar');
  const [isExporting, setIsExporting] = useState(false);

  // Filter states
  const [filters, setFilters] = useState<YearlyCalendarReportFilters>({
    startDate: formatDateToLocalString(new Date(new Date().getFullYear(), 0, 1)),
    endDate: formatDateToLocalString(new Date(new Date().getFullYear(), 11, 31)),
    pageNumber: 1,
    pageSize: 50
  });

  // Calendar navigation states
  const [startDateCalendar, setStartDateCalendar] = useState<Date>(
    new Date(new Date().getFullYear(), 0, 1)
  );
  const [endDateCalendar, setEndDateCalendar] = useState<Date>(
    new Date(new Date().getFullYear(), 11, 31)
  );

  const {
    data: reportData,
    isLoading,
    error
  } = useQuery({
    queryKey: ['yearlyCalendarReports', filters],
    queryFn: () => yearlyCalendarReportService.getCourseReportList(filters),
    refetchOnWindowFocus: false
  });

  const { data: masterArea } = useQuery({
    queryKey: ['masterAreas'],
    queryFn: () => masterAreaService.getList({ limit: 100 }),
    refetchOnWindowFocus: false
  });

  const areaOptions = useMemo(() => {
    const areas = masterArea?.data
      ? masterArea.data.map((area) => ({
          value: area.areaID || '',
          label: area.areaName || 'Unnamed Area'
        }))
      : [];
    return [{ value: '', label: 'All Area' }, ...areas];
  }, [masterArea]);

  const courses: YearlyCalendarCourseReport[] = reportData?.data || [];

  const transformedCourses = useMemo(() => {
    return courses.map(
      (course: YearlyCalendarCourseReport): ScheduleCourse => ({
        msLCMSCourseID: course.msLCMSCourseID || '',
        courseName: course.courseName || 'Untitled Course',
        courseCode: course.courseCode || '',
        learningHours: course.learningHours || 0,
        deliveryType: course.modeOfDelivery || 'Unknown',
        planningStart: course.planningStart,
        planningEnd: course.planningEnd,
        trLCMSPlanningCoursesID: course.trLCMSPlanningCoursesID || course.msLCMSCourseID || ''
      })
    );
  }, [courses]);

  // Calculate statistics
  const statistics = useMemo(() => {
    return yearlyCalendarReportService.calculateStatistics(courses);
  }, [courses]);

  // Filter options
  const modeOfDeliveryOptions = [
    { value: '', label: 'All Mode of Delivery' },
    { value: 'Virtual Conference', label: 'Virtual Conference' },
    { value: 'Face to Face', label: 'Face to Face' },
    { value: 'Blended Learning', label: 'Blended Learning' },
    { value: 'Self Learning', label: 'Self Learning' },
    { value: 'On The Job Training (OJT)', label: 'On The Job Training (OJT)' },
    { value: 'Hybrid Learning', label: 'Hybrid Learning' },
    { value: 'Experiential Learning', label: 'Experiential Learning' }
  ];

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'planned', label: 'Planned' },
    { value: 'canceled', label: 'Canceled' },
    { value: 'dropped', label: 'Dropped' }
  ];

  const planStatusOptions = [
    { value: '', label: 'All Plan Statuses' },
    { value: 'planned', label: 'Planned' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' }
  ];

  // Handlers
  const handleFilterChange = (key: keyof YearlyCalendarReportFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      pageNumber: 1
    }));
  };

  const handleExportToExcel = async () => {
    try {
      setIsExporting(true);

      const exportFilters = {
        startDate: filters.startDate,
        endDate: filters.endDate,
        planStatus: filters.planStatus,
        deliveryType: filters.deliveryType,
        courseStatus: filters.courseStatus,
        schoolName: filters.schoolName,
        search: filters.search,
        schoolID: filters.schoolID
      };

      await yearlyCalendarReportService.exportCalendarToExcel(exportFilters);

      notifications.show({
        title: 'Export Successful',
        message: 'Yearly calendar has been exported to Excel successfully',
        color: 'green',
        icon: <IconFileSpreadsheet size={16} />
      });
    } catch {
      notifications.show({
        title: 'Export Failed',
        message: 'Failed to export yearly calendar to Excel',
        color: 'red'
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Only show error alert for non-404 errors
  const is404Error = error && (error as any)?.response?.status === 404;

  if (error && !is404Error) {
    return (
      <Stack gap='md'>
        <Alert icon={<IconChartBar size={16} />} title='Error' color='red'>
          Failed to load report data. Please try again later.
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap='md'>
      <PageTitle
        title='Yearly Calendar Reports'
        description='View and analyze course scheduling data with comprehensive reporting tools'
      />

      {/* Filters */}
      <Paper p='md' radius='md' withBorder>
        <Grid>
          <Grid.Col span={12}>
            <Group justify='space-between' mb='md'>
              <Group gap='sm'>
                <IconFilter size={20} />
                <Text fw={500}>Filters</Text>
              </Group>
            </Group>
          </Grid.Col>

          <Grid.Col span={{ base: 6, md: 3 }}>
            <DatePickerInput
              label='Start Date'
              value={parseLocalDateString(filters.startDate)}
              date={startDateCalendar}
              onDateChange={setStartDateCalendar}
              onChange={(date) => {
                if (date) {
                  handleFilterChange('startDate', formatDateToLocalString(date));
                  setStartDateCalendar(date);
                } else {
                  handleFilterChange('startDate', '');
                }
              }}
              clearable
            />
          </Grid.Col>

          <Grid.Col span={{ base: 6, md: 3 }}>
            <DatePickerInput
              label='End Date'
              value={parseLocalDateString(filters.endDate)}
              date={endDateCalendar}
              onDateChange={setEndDateCalendar}
              onChange={(date) => {
                if (date) {
                  handleFilterChange('endDate', formatDateToLocalString(date));
                  setEndDateCalendar(date);
                } else {
                  handleFilterChange('endDate', '');
                }
              }}
              clearable
            />
          </Grid.Col>

          <Grid.Col span={{ base: 6, md: 3 }}>
            <Select
              label='Plan Status'
              data={planStatusOptions}
              value={filters.planStatus || ''}
              onChange={(value) => handleFilterChange('planStatus', value)}
              clearable
            />
          </Grid.Col>

          <Grid.Col span={{ base: 6, md: 3 }}>
            <Select
              label='Mode of Delivery'
              data={modeOfDeliveryOptions}
              value={filters.deliveryType || ''}
              onChange={(value) => handleFilterChange('deliveryType', value)}
              clearable
            />
          </Grid.Col>

          <Grid.Col span={{ base: 6, md: 6 }}>
            <Select
              label='Course Status'
              data={statusOptions}
              value={filters.courseStatus || ''}
              onChange={(value) => handleFilterChange('courseStatus', value)}
              clearable
            />
          </Grid.Col>

          <Grid.Col span={{ base: 6, md: 6 }}>
            <Select
              label='Select Area'
              data={areaOptions}
              value={filters.schoolID ?? ''}
              onChange={(value) => handleFilterChange('schoolID', value ?? '')}
              clearable
            />
          </Grid.Col>
        </Grid>
      </Paper>

      {/* View Controls */}
      <Paper p='md' radius='md' withBorder>
        <Group justify='space-between'>
          <SegmentedControl
            value={viewMode}
            onChange={(value) => setViewMode(value as 'calendar' | 'statistics')}
            data={[
              { label: 'Calendar View', value: 'calendar' },
              { label: 'Statistics', value: 'statistics' }
            ]}
          />

          <Group gap='sm'>
            <Button
              leftSection={<IconDownload size={16} />}
              onClick={handleExportToExcel}
              loading={isExporting}
              variant='light'
            >
              Export Excel
            </Button>
          </Group>
        </Group>
      </Paper>

      {/* Summary Stats */}
      <Paper p='md' radius='md' withBorder>
        <Group>
          <Badge variant='light' color='blue' size='lg'>
            {courses.length} courses
          </Badge>
          <Badge variant='light' color='green' size='lg'>
            {statistics.totalLearningHours} total hours
          </Badge>
          <Badge variant='light' color='yellow' size='lg'>
            {statistics.plannedCourses} planned
          </Badge>
          {statistics.canceledCourses > 0 && (
            <Badge variant='light' color='red' size='lg'>
              {statistics.canceledCourses} canceled
            </Badge>
          )}
          {statistics.droppedCourses > 0 && (
            <Badge variant='light' color='orange' size='lg'>
              {statistics.droppedCourses} dropped
            </Badge>
          )}
        </Group>
      </Paper>

      {/* Content */}
      {isLoading ? (
        <Center h={400}>
          <Loader size='lg' />
        </Center>
      ) : courses.length === 0 ? (
        <Paper p='xl' radius='md' withBorder>
          <Center h={300}>
            <Stack align='center' gap='md'>
              <IconChartBar size={48} color='gray' />
              <Text size='lg' fw={500} c='dimmed'>
                No Data Found
              </Text>
              <Text size='sm' c='dimmed' ta='center'>
                No course data matches your current filter criteria.
                <br />
                Try adjusting the filters to see more results.
              </Text>
            </Stack>
          </Center>
        </Paper>
      ) : (
        <>
          {viewMode === 'calendar' && (
            <ReadOnlyCalendarScheduleView
              courses={transformedCourses}
              initialDate={parseLocalDateString(filters.startDate) || undefined}
              filterStartDate={parseLocalDateString(filters.startDate)}
              filterEndDate={parseLocalDateString(filters.endDate)}
            />
          )}

          {viewMode === 'statistics' && (
            <ReportStatistics statistics={statistics} courses={courses} />
          )}
        </>
      )}
    </Stack>
  );
};

// Interface to match ReadOnlyCalendarScheduleView expectations
interface ScheduleCourse {
  msLCMSCourseID: string;
  courseName: string;
  courseCode: string;
  learningHours: number;
  deliveryType?: string;
  planningStart: string | null;
  planningEnd: string | null;
  trLCMSPlanningCoursesID: string;
}

export default ReportsPage;
